// import { calculateDistance } from './geoCalculations';

/**
 * Calculate destination point given a starting point, distance and bearing
 * @param {Object} point - Start point {lat, lng}
 * @param {number} distance - Distance in meters
 * @param {number} bearing - Bearing in radians
 * @param {number} R - Earth radius in meters
 * @returns {Object} Destination point {lat, lng}
 */
function destinationPoint(point, distance, bearing, R = 6371000) {
  const lat1 = point.lat * Math.PI / 180;
  const lng1 = point.lng * Math.PI / 180;

  const lat2 = Math.asin(
    Math.sin(lat1) * Math.cos(distance / R) +
    Math.cos(lat1) * Math.sin(distance / R) * Math.cos(bearing)
  );

  const lng2 = lng1 + Math.atan2(
    Math.sin(bearing) * Math.sin(distance / R) * Math.cos(lat1),
    Math.cos(distance / R) - Math.sin(lat1) * Math.sin(lat2)
  );

  return {
    lat: lat2 * 180 / Math.PI,
    lng: lng2 * 180 / Math.PI
  };
}

/**
 * Calculate plant positions in a grid within a crop area (slot)
 * @param {Object} slot - Crop area object with corners array
 * @param {number} rowSpacing - Distance between rows in meters (along length direction)
 * @param {number} colSpacing - Distance between columns in meters (along width direction)
 * @param {number} rowMargin - Margin from edge to first row in meters (applied to both sides)
 * @param {number} colMargin - Margin from edge to first column in meters (applied to both sides)
 * @param {number} numRows - Number of rows (0 means as many as fit)
 * @param {number} numCols - Number of columns (0 means as many as fit)
 * @returns {Array} Array of plant positions with GPS coordinates
 */
function calculatePlantPositions(slot, rowSpacing, colSpacing, rowMargin, colMargin, numRows, numCols) {
  if (!slot || !slot.corners || slot.corners.length !== 4) {
    return [];
  }

  const R = 6371000; // Earth's radius in meters

  // Use the first two corner points to define the direction
  const corner1 = slot.corners[0]; // Start corner (bottom-left)
  const corner2 = slot.corners[1]; // End corner (bottom-right, defines length direction)
  const corner4 = slot.corners[3]; // Defines width direction

  const length = slot.length; // Distance along the main axis (first direction created)
  const width = slot.width;   // Distance perpendicular to main axis

  // Calculate bearing from corner1 to corner2 (length direction)
  const lengthBearing = Math.atan2(
    Math.sin((corner2.lng - corner1.lng) * Math.PI / 180) * Math.cos(corner2.lat * Math.PI / 180),
    Math.cos(corner1.lat * Math.PI / 180) * Math.sin(corner2.lat * Math.PI / 180) -
    Math.sin(corner1.lat * Math.PI / 180) * Math.cos(corner2.lat * Math.PI / 180) *
    Math.cos((corner2.lng - corner1.lng) * Math.PI / 180)
  );

  // Calculate bearing from corner1 to corner4 (width direction)
  const widthBearing = Math.atan2(
    Math.sin((corner4.lng - corner1.lng) * Math.PI / 180) * Math.cos(corner4.lat * Math.PI / 180),
    Math.cos(corner1.lat * Math.PI / 180) * Math.sin(corner4.lat * Math.PI / 180) -
    Math.sin(corner1.lat * Math.PI / 180) * Math.cos(corner4.lat * Math.PI / 180) *
    Math.cos((corner4.lng - corner1.lng) * Math.PI / 180)
  );

  // Available space after accounting for margin on both sides
  // ROWS go along the LENGTH direction (first direction created)
  // COLUMNS go along the WIDTH direction (perpendicular)
  const availableLength = length - 2 * rowMargin;
  const availableWidth = width - 2 * colMargin;

  // Calculate how many plants fit
  // Add 1 to account for the first plant (spacing counts intervals, not plants)
  const maxRows = availableLength > 0 ? Math.floor(availableLength / rowSpacing) + 1 : 0;
  const maxCols = availableWidth > 0 ? Math.floor(availableWidth / colSpacing) + 1 : 0;

  const actualRows = numRows === 0 ? maxRows : Math.min(numRows, maxRows);
  const actualCols = numCols === 0 ? maxCols : Math.min(numCols, maxCols);

  const plants = [];
  let plantIndex = 1;

  for (let row = 0; row < actualRows; row++) {
    for (let col = 0; col < actualCols; col++) {
      // Calculate distance along each axis, starting from the margin position
      // Rows go along length direction, columns go along width direction
      const distanceAlongLength = rowMargin + (row * rowSpacing);
      const distanceAlongWidth = colMargin + (col * colSpacing);

      // Start from corner1 and move along length direction
      const pointAlongLength = destinationPoint(corner1, distanceAlongLength, lengthBearing, R);

      // Then move along width direction from that point
      const plantPosition = destinationPoint(pointAlongLength, distanceAlongWidth, widthBearing, R);

      plants.push({
        index: plantIndex++,
        position: plantPosition,
        row: row + 1,
        col: col + 1
      });
    }
  }

  return plants;
}

/**
 * Calculate the total number of plants that will fit in a slot
 * @param {Object} slot - Crop area object
 * @param {number} rowSpacing - Distance between rows in meters (along length direction)
 * @param {number} colSpacing - Distance between columns in meters (along width direction)
 * @param {number} rowMargin - Margin from edge to first row in meters (applied to both sides)
 * @param {number} colMargin - Margin from edge to first column in meters (applied to both sides)
 * @param {number} numRows - Number of rows (0 means as many as fit)
 * @param {number} numCols - Number of columns (0 means as many as fit)
 * @returns {Object} Object with rows, cols, and total count
 */
function calculatePlantCount(slot, rowSpacing, colSpacing, rowMargin, colMargin, numRows, numCols) {
  if (!slot || rowSpacing <= 0 || colSpacing <= 0) {
    return { rows: 0, cols: 0, total: 0 };
  }

  // Available space after accounting for margin on both sides
  // ROWS go along the LENGTH direction (first direction created)
  // COLUMNS go along the WIDTH direction (perpendicular)
  const availableLength = slot.length - 2 * rowMargin;
  const availableWidth = slot.width - 2 * colMargin;

  // Calculate max rows and cols considering the margin
  // Add 1 to account for the first plant (spacing counts intervals, not plants)
  const maxRows = availableLength > 0 ? Math.floor(availableLength / rowSpacing) + 1 : 0;
  const maxCols = availableWidth > 0 ? Math.floor(availableWidth / colSpacing) + 1 : 0;

  const actualRows = numRows === 0 ? maxRows : Math.min(numRows, maxRows);
  const actualCols = numCols === 0 ? maxCols : Math.min(numCols, maxCols);

  return {
    rows: actualRows,
    cols: actualCols,
    total: actualRows * actualCols
  };
}

export { calculatePlantPositions, calculatePlantCount, destinationPoint };

// CommonJS exports for Firebase Functions compatibility
if (typeof module !== 'undefined' && module.exports) {
  module.exports = { calculatePlantPositions, calculatePlantCount, destinationPoint };
}
